import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../superadmin/AddDistrict.css';

const StudentIntakeForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    last_4_ssn: '',
    date_of_birth: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }

    if (formData.last_4_ssn && !/^\d{4}$/.test(formData.last_4_ssn)) {
      setError('Last 4 digits of SSN must be exactly 4 numbers');
      return false;
    }

    if (formData.date_of_birth) {
      const dob = new Date(formData.date_of_birth);
      const now = new Date();
      if (isNaN(dob.getTime())) {
        setError('Please enter a valid date of birth');
        return false;
      }
      if (dob > now) {
        setError('Date of birth cannot be in the future');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Clean up the data
      const submitData = {
        ...formData,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        last_4_ssn: formData.last_4_ssn.trim() || null,
        date_of_birth: formData.date_of_birth || null
      };

      const response = await axios.post('/api/student-intake', submitData);

      // Navigate back to student intake list
      navigate('/parent/student-intake');
    } catch (error) {
      console.error('Error creating student intake:', error);
      if (error.response?.status === 409) {
        setError(error.response.data.error || 'A student with this information already exists');
      } else {
        setError(error.response?.data?.error || 'Failed to create student record');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/parent/student-intake');
  };

  return (
    <div className="add-district">
      <div className="page-header">
        <h2>Add New Student</h2>
        <p>Enter basic information about your student. You can complete forms for this student later.</p>
      </div>

      <form onSubmit={handleSubmit} className="district-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">First Name *</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              maxLength="100"
              placeholder="Enter first name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">Last Name *</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              maxLength="100"
              placeholder="Enter last name"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="last_4_ssn">Last 4 Digits of SSN *</label>
            <input
              type="text"
              id="last_4_ssn"
              name="last_4_ssn"
              value={formData.last_4_ssn}
              onChange={handleInputChange}
              required
              maxLength="4"
              pattern="\d{4}"
              placeholder="1234"
              title="Enter exactly 4 digits"
            />
            <small style={{ color: '#6c757d', fontSize: '0.875em' }}>
              Required: Used for matching with school records
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="date_of_birth">Date of Birth *</label>
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleInputChange}
              required
              max={new Date().toISOString().split('T')[0]} // Cannot be in the future
            />
            <small style={{ color: '#6c757d', fontSize: '0.875em' }}>
              Required: Used to calculate age and ensure accurate records
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Add Student'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentIntakeForm;

